import { useStore } from '@xyflow/react';
import { ZfIconButton, ZfTooltip } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NodeVisibility } from '../../../app/api/generated/client';
import { useShadowRootCustomStyles } from '../../../app/hooks/useShadowRootCustomStyles';
import { useUpdateNodeVisibility } from '../../api/mutations';
import { usePrevious } from '../../hooks';

interface INodeVisibilityButtonProps {
    partnerUuid: string;
    nodeUuid: string;
    visibility?: NodeVisibility;
}

export const NodeVisibilityButton: FunctionComponent<INodeVisibilityButtonProps> = ({ partnerUuid, nodeUuid, visibility }) => {
    const { t } = useTranslation();
    const zoom = useStore((state) => state.transform[2]);

    const tooltipRef = useRef<HTMLZfTooltipElement>(null);
    const [nodeVisibility, setNodeVisibility] = useState<NodeVisibility>(visibility ?? 'VISIBLE');
    const prevVisibility = usePrevious(visibility);

    const tooltipText = useMemo(() => t(`familyTreeVisibility-${nodeVisibility}`), [nodeVisibility, t]);

    const { mutate: updateNodeVisibility } = useUpdateNodeVisibility(partnerUuid);

    useEffect(() => {
        if (visibility != null && visibility !== prevVisibility) setNodeVisibility(visibility);
    }, [visibility, prevVisibility]);

    const onVisibilityClick = useCallback(() => {
        const newVisibility = nodeVisibility === 'VISIBLE' ? 'HIDDEN' : 'VISIBLE';
        setNodeVisibility(newVisibility);
        updateNodeVisibility({ nodeUuid, newVisibility });
    }, [nodeUuid, nodeVisibility, updateNodeVisibility]);

    useShadowRootCustomStyles(tooltipRef, `.tooltip { margin-top: ${-3 / zoom}px !important; }`);

    return (
        <ZfTooltip ref={tooltipRef} text={tooltipText}>
            <ZfIconButton
                slot="anchor"
                icon={nodeVisibility === 'VISIBLE' ? 'visibility' : 'visibility_off'}
                hierarchy="tertiary"
                size="extra-small"
                disabled={nodeVisibility === 'HIDDEN_BY_RELATION'}
                onClick={onVisibilityClick}
            />
        </ZfTooltip>
    );
};
